import { searchClient } from '@/lib/search/instantsearch/algoliaAdapter';

/**
 * Multi-index search service for querying both sequences and displays simultaneously
 * Used by the header search modal for unified search experience
 */

export interface MultiIndexSearchResult {
  sequences: any[];
  displays: any[];
  sequenceCount: number;
  displayCount: number;
}

/**
 * Query both sequences and displays indexes in a single Algolia API call
 * @param query - Search query string
 * @returns Object containing hits and counts for both indexes
 */
export async function getMultiIndexSuggestions(query: string): Promise<MultiIndexSearchResult> {
  // Return empty results for short queries
  if (!searchClient || !query.trim() || query.length < 2) {
    return {
      sequences: [],
      displays: [],
      sequenceCount: 0,
      displayCount: 0,
    };
  }

  try {
    // Perform multi-index search with single API call
    const { results } = await searchClient.search([
      {
        indexName: 'sequences',
        query: query.trim(),
        params: {
          hitsPerPage: 5,
          attributesToRetrieve: [
            'objectID',
            'title',
            'artist_name',
            'image_url',
            'thumbnail_url',
          ],
          highlightPreTag: '<mark>',
          highlightPostTag: '</mark>',
          attributesToHighlight: ['title', 'artist_name'],
          analytics: false,
        },
      },
      {
        indexName: 'displays',
        query: query.trim(),
        params: {
          hitsPerPage: 5,
          attributesToRetrieve: [
            'objectID',
            'display_name',
            'name',
            'city',
            'state',
          ],
          highlightPreTag: '<mark>',
          highlightPostTag: '</mark>',
          attributesToHighlight: ['display_name', 'name', 'city'],
          analytics: false,
        },
      },
    ]);

    return {
      sequences: results[0]?.hits || [],
      displays: results[1]?.hits || [],
      sequenceCount: results[0]?.nbHits || 0,
      displayCount: results[1]?.nbHits || 0,
    };
  } catch (error) {
    console.error('Algolia multi-index search error:', {
      error,
      query: query.trim(),
      timestamp: new Date().toISOString(),
    });
    return {
      sequences: [],
      displays: [],
      sequenceCount: 0,
      displayCount: 0,
    };
  }
}
